import { NavLink, useNavigate, useLocation } from "react-router-dom"
import {
  AcademicCapIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftEllipsisIcon,
  ChatBubbleLeftRightIcon,
  BanknotesIcon,
  AdjustmentsHorizontalIcon,
  UsersIcon,
  FlagIcon,
  ChartBarIcon,
  HomeIcon,
  UserPlusIcon,
  BellIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/16/solid"
import { ArrowRightOnRectangleIcon } from "@heroicons/react/20/solid"
import { ReactNode, useState } from "react"

type Props = {
  title: string
  children: ReactNode
}

type NavAccent = {
  icon: string
  bg: string
  text: string
  bar: string
}

type NavNode = {
  to?: string
  label: string
  icon?: ReactNode
  children?: NavNode[]
  /** Per-item accent color (icon always tinted, active state uses it instead of default blue) */
  accent?: NavAccent
}

const ACCENT = {
  violet:  { icon: "text-violet-500",  bg: "bg-violet-50",  text: "text-violet-700",  bar: "bg-violet-600" },
  emerald: { icon: "text-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700", bar: "bg-emerald-600" },
  red:     { icon: "text-red-500",     bg: "bg-red-50",     text: "text-red-700",     bar: "bg-red-600" },
  orange:  { icon: "text-orange-500",  bg: "bg-orange-50",  text: "text-orange-700",  bar: "bg-orange-600" },
  sky:     { icon: "text-sky-500",     bg: "bg-sky-50",     text: "text-sky-700",     bar: "bg-sky-600" },
  amber:   { icon: "text-amber-500",   bg: "bg-amber-50",   text: "text-amber-700",   bar: "bg-amber-600" },
} as const satisfies Record<string, NavAccent>

type NavGroup = {
  label?: string
  items: NavNode[]
}

const ADMIN_NAV: NavGroup[] = [
  {
    items: [
      { to: "/dashboard", label: "Главная", icon: <HomeIcon className="w-4 h-4" /> },
    ],
  },
  {
    label: "Контент",
    items: [
      {
        label: "Курсы",
        icon: <AcademicCapIcon className="w-4 h-4" />,
        accent: ACCENT.violet,
        children: [
          { to: "/courses",    label: "Список курсов" },
          { to: "/categories", label: "Категории" },
        ],
      },
      { to: "/vacancies",         label: "Вакансии",             icon: <BriefcaseIcon className="w-4 h-4" />,     accent: ACCENT.emerald },
      { to: "/china-enterprises", label: "Китайские предприятия", icon: <BuildingOffice2Icon className="w-4 h-4" />, accent: ACCENT.red },
    ],
  },
  {
    label: "Сообщество",
    items: [
      { to: "/users",   label: "Пользователи",   icon: <UsersIcon className="w-4 h-4" /> },
      { to: "/admin/notifications", label: "Push-уведомления", icon: <BellIcon className="w-4 h-4" /> },
      {
        to: "/reports",
        label: "Жалобы",
        icon: <FlagIcon className="w-4 h-4" />,
        children: [
          { to: "/reports/user",   label: "Пользователи" },
          { to: "/reports/job",    label: "Вакансии" },
          { to: "/reports/course", label: "Курсы" },
          { to: "/reports/china",  label: "Китайские предприятия" },
        ],
      },
      {
        to: "/admin/reviews",
        label: "Отзывы",
        icon: <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />,
        children: [
          { to: "/admin/reviews/course",     label: "Курсы" },
          { to: "/admin/reviews/enterprise", label: "Китайские предприятия" },
        ],
      },
    ],
  },
  {
    label: "Платежи",
    items: [
      {
        label: "Биллинг",
        icon: <BanknotesIcon className="w-4 h-4" />,
        accent: ACCENT.sky,
        children: [
          { to: "/admin/orders",             label: "Заказы" },
          { to: "/admin/payments",           label: "Платежи" },
          { to: "/admin/subscriptions",      label: "Подписки" },
          { to: "/admin/subscription-plans", label: "Тарифы" },
          { to: "/admin/iap-tiers",          label: "IAP-уровни" },
        ],
      },
      { to: "/admin/limits", label: "Лимиты", icon: <AdjustmentsHorizontalIcon className="w-4 h-4" />, accent: ACCENT.amber },
    ],
  },
  {
    label: "Аналитика",
    items: [
      { to: "/admin/analytics", label: "Аналитика", icon: <ChartBarIcon className="w-4 h-4" /> },
      { to: "/admin/referrals", label: "Рефералы",  icon: <UserPlusIcon className="w-4 h-4" /> },
    ],
  },
]

const MENTOR_NAV: NavGroup[] = [
  {
    items: [
      { to: "/profile", label: "Профиль", icon: <UsersIcon className="w-4 h-4" /> },
    ],
  },
  {
    label: "Обучение",
    items: [
      { to: "/mentor/courses",   label: "Мои курсы", icon: <BookOpenIcon className="w-4 h-4" /> },
      { to: "/mentor/analytics", label: "Аналитика",  icon: <ChartBarIcon className="w-4 h-4" /> },
    ],
  },
  {
    label: "Общение",
    items: [
      { to: "/chats", label: "Чат", icon: <ChatBubbleLeftRightIcon className="w-4 h-4" /> },
    ],
  },
]

const ROLE_LABELS: Record<string, string> = { admin: "Админ", mentor: "Ментор" }

/** Walks the nav tree and returns the chain of nodes leading to (and including) the deepest match for `pathname`. */
const findNavChain = (nodes: NavNode[], pathname: string): NavNode[] | null => {
  for (const node of nodes) {
    if (node.children?.length) {
      const childChain = findNavChain(node.children, pathname)
      if (childChain) return [node, ...childChain]
    }
    if (node.to && pathname.startsWith(node.to)) return [node]
  }
  return null
}

export const DashboardLayout = ({ title, children }: Props) => {
  const navigate = useNavigate()
  const location = useLocation()
  const role = localStorage.getItem("role")

  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({})

  const navKey = (node: NavNode) => node.to ?? node.label

  /** True if this node's own route, or any descendant's route, matches the current path. */
  const nodeMatchesLocation = (node: NavNode): boolean => {
    if (node.to && location.pathname.startsWith(node.to)) return true
    return !!node.children?.some(nodeMatchesLocation)
  }

  const isExpanded = (node: NavNode) => {
    if (!node.children?.length) return false
    const key = navKey(node)
    if (key in expandedOverrides) return expandedOverrides[key]
    return nodeMatchesLocation(node)
  }

  const toggleExpanded = (node: NavNode) => {
    const key = navKey(node)
    setExpandedOverrides((prev) => ({ ...prev, [key]: !isExpanded(node) }))
  }

  const navRowClass = (depth: number, active: boolean, accent?: NavAccent) =>
    depth === 0
      ? `relative flex items-center gap-2 pl-2.5 pr-1.5 py-[6px] rounded-md text-[13px] font-medium transition-colors ${
          active ? `${accent?.bg ?? "bg-blue-50"} ${accent?.text ?? "text-blue-700"}` : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`
      : `flex items-center gap-2 pl-2.5 pr-2.5 py-[5px] rounded-md text-[12.5px] font-medium truncate transition-colors ${
          active ? `${accent?.bg ?? "bg-blue-50"} ${accent?.text ?? "text-blue-700"}` : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
        }`

  const renderNavNode = (node: NavNode, depth: number): ReactNode => {
    const hasChildren = !!node.children?.length
    const expanded = hasChildren && isExpanded(node)
    const childActive = hasChildren && node.children!.some(nodeMatchesLocation)

    return (
      <div key={navKey(node)}>
        {node.to ? (
          <NavLink to={node.to} end={!hasChildren} className={({ isActive }) => navRowClass(depth, isActive, node.accent)}>
            {({ isActive }) => (
              <>
                {isActive && !hasChildren && depth === 0 && (
                  <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-r-full ${node.accent?.bar ?? "bg-blue-600"}`} />
                )}
                {node.icon && (
                  <span className={`flex-shrink-0 ${node.accent ? node.accent.icon : isActive ? "text-blue-600" : "text-gray-400"}`}>{node.icon}</span>
                )}
                <span className="truncate flex-1">{node.label}</span>
                {hasChildren && (
                  <span
                    role="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleExpanded(node) }}
                    className="p-1 rounded hover:bg-black/5 flex-shrink-0"
                  >
                    <ChevronDownIcon
                      className={`w-3 h-3 text-gray-400 transition-transform duration-150 ${expanded ? "rotate-180" : ""}`}
                    />
                  </span>
                )}
              </>
            )}
          </NavLink>
        ) : (
          <button
            type="button"
            onClick={() => toggleExpanded(node)}
            className={`w-full text-left ${navRowClass(depth, childActive, node.accent)}`}
          >
            {node.icon && (
              <span className={`flex-shrink-0 ${node.accent ? node.accent.icon : childActive ? "text-blue-600" : "text-gray-400"}`}>{node.icon}</span>
            )}
            <span className="truncate flex-1">{node.label}</span>
            {hasChildren && (
              <ChevronDownIcon
                className={`w-3 h-3 text-gray-400 transition-transform duration-150 flex-shrink-0 ${expanded ? "rotate-180" : ""}`}
              />
            )}
          </button>
        )}

        {hasChildren && expanded && (
          <div className="ml-[21px] pl-2.5 border-l border-gray-100 mt-0.5 mb-0.5 space-y-px">
            {node.children!.map((child) => renderNavNode(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  const logout = () => {
    localStorage.clear()
    navigate("/login", { replace: true })
  }

  const nav = role === "admin" ? ADMIN_NAV : MENTOR_NAV

  const navChain = findNavChain(nav.flatMap((g) => g.items), location.pathname)
  const breadcrumbLabels = (navChain ?? []).slice(0, -1).map((n) => n.label)

  return (
    <div className="min-h-screen flex bg-gray-50">

      {/* ── SIDEBAR ── */}
      {role && (
        <aside className="w-60 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 sticky top-0 h-screen">

          {/* Brand */}
          <NavLink to={role === "admin" ? "/dashboard" : "/profile"} className="h-14 flex items-center px-4 border-b border-gray-200 hover:bg-gray-50 transition-colors">
            <img src="/icon.png" alt="Vkonekte" className="h-9 w-9 object-contain" />
          </NavLink>

          {/* Nav */}
          <nav className="flex-1 px-2 py-3 overflow-y-auto">
            {nav.map((group, i) => (
              <div key={group.label ?? i} className="mb-3">
                {group.label && (
                  <div className="px-2.5 mb-1 mt-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    {group.label}
                  </div>
                )}
                <div className="space-y-px">
                  {group.items.map((item) => renderNavNode(item, 0))}
                </div>
              </div>
            ))}
          </nav>

        </aside>
      )}

      {/* ── MAIN ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Top header */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6 sticky top-0 z-10 gap-3">
          <div className="flex items-center gap-1.5 min-w-0">
            {breadcrumbLabels.map((label, i) => (
              <span key={i} className="flex items-center gap-1.5 text-sm text-gray-400 flex-shrink-0">
                {label}
                <ChevronRightIcon className="w-3.5 h-3.5 text-gray-300" />
              </span>
            ))}
            <h1 className="text-base font-semibold text-gray-900 truncate">{title}</h1>
          </div>

          {role && (
            <div className="ml-auto flex items-center gap-1 flex-shrink-0">
              <div className="flex items-center gap-2 pl-1 pr-3 py-1">
                <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-blue-600">{(ROLE_LABELS[role] ?? role).charAt(0)}</span>
                </div>
                <span className="text-sm font-medium text-gray-700">{ROLE_LABELS[role] ?? role}</span>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Выйти
              </button>
            </div>
          )}
        </header>

        {/* Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
